/**
 * OW Animation Module - Observation well diagram
 * Elevasi + muka air terhadap top pipa only (no pump/sensor).
 *
 * @requires D3.js
 * @requires moment.js
 * @requires DashboardConfig
 * @requires DashboardAPI
 */

const OwAnimationModule = (() => {
  const SVG_URL = "/images/well-diagram-ow.svg?v=4";
  const LAYOUT = {
    wellTop: 98,
    wellHeight: 272,
    wellBottom: 370,
    waterDefaultY: 200,
    wellLeftX: 168,
    wellRightX: 232,
  };

  const PROXIMITY_PX = 50;
  const CARD_GAP = 8;
  const STACK_OFFSET = 16;
  const KNEE_INSET = 8;

  const CALLOUTS = {
    elev: {
      cardEdgeX: 120,
      defaultCenterY: 173,
      cardHeight: 58,
      groupId: "ow-label-elev-group",
      connectorId: "ow-elev-connector",
      anchorId: "ow-elev-anchor",
      anchorX: LAYOUT.wellLeftX,
      side: "left",
    },
    wl: {
      cardEdgeX: 276,
      defaultCenterY: 171,
      cardHeight: 66,
      groupId: "ow-label-wl-group",
      connectorId: "ow-wl-connector",
      anchorId: "ow-wl-anchor",
      anchorX: LAYOUT.wellRightX,
      side: "right",
    },
  };

  let _wellConfig = {
    boreholeDepth: null,
    topPipeElevation: null,
    unitDisplay: "m",
  };
  let _reading = null;
  let _deviceId = null;
  let _pollInterval = null;
  let _templateLoaded = false;

  const _fmt = (value) => {
    if (typeof DashboardHelpers !== "undefined" && DashboardHelpers.formatNumber) {
      return DashboardHelpers.formatNumber(value);
    }
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("id-ID", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  };

  const _toMeters = (value, unit) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return String(unit || "m").toLowerCase() === "cm" ? n / 100 : n;
  };

  /** Kedalaman dari top pipa (m) → koordinat Y SVG */
  const _depthToY = (depthM) =>
    LAYOUT.wellTop + (depthM / _wellConfig.boreholeDepth) * LAYOUT.wellHeight;

  const _clampCardCenterY = (centerY, cardHeight) => {
    const min = LAYOUT.wellTop - 10 + cardHeight / 2;
    const max = LAYOUT.wellBottom + 30 - cardHeight / 2;
    return Math.max(min, Math.min(max, centerY));
  };

  const _layoutPair = (anchorA, anchorB, heightA, heightB) => {
    let centerA;
    let centerB;

    if (Math.abs(anchorA - anchorB) < PROXIMITY_PX) {
      const mid = (anchorA + anchorB) / 2;
      centerA = mid - STACK_OFFSET - heightA / 2;
      centerB = mid + STACK_OFFSET + heightB / 2;
    } else {
      centerA = anchorA;
      centerB = anchorB;
    }

    const minDist = heightA / 2 + heightB / 2 + CARD_GAP;
    if (Math.abs(centerA - centerB) < minDist) {
      const shift = (minDist - Math.abs(centerA - centerB)) / 2 + 1;
      if (centerA <= centerB) {
        centerA -= shift;
        centerB += shift;
      } else {
        centerA += shift;
        centerB -= shift;
      }
    }

    return {
      a: _clampCardCenterY(centerA, heightA),
      b: _clampCardCenterY(centerB, heightB),
    };
  };

  const _isConfigReady = () => _wellConfig.boreholeDepth > 0;

  const _showPanel = () => {
    const panel = document.getElementById("ow-well-diagram-panel");
    if (panel) panel.style.display = "block";
  };

  const _hidePanel = () => {
    const panel = document.getElementById("ow-well-diagram-panel");
    if (panel) panel.style.display = "none";

    const content = document.getElementById("ow-well-diagram-content");
    const empty = document.getElementById("ow-well-diagram-empty");
    const diagram = document.getElementById("ow-diagram");

    if (content) content.style.display = "none";
    if (empty) empty.style.display = "none";
    if (diagram) diagram.style.display = "none";

    _hideShimmer();
  };

  const _showDiagramContent = () => {
    _showPanel();

    const content = document.getElementById("ow-well-diagram-content");
    const empty = document.getElementById("ow-well-diagram-empty");

    if (content) content.style.display = "block";
    if (empty) empty.style.display = "none";
  };

  const _showEmptyState = (
    title = "Data Sumur Belum Diatur",
    description = "Ilustrasi observation well tidak dapat ditampilkan karena kedalaman borehole belum diatur.",
  ) => {
    _showPanel();
    _hideShimmer();

    const content = document.getElementById("ow-well-diagram-content");
    const empty = document.getElementById("ow-well-diagram-empty");
    const titleEl = document.getElementById("ow-well-diagram-empty-title");
    const descEl = document.getElementById("ow-well-diagram-empty-desc");
    const diagram = document.getElementById("ow-diagram");

    if (content) content.style.display = "none";
    if (empty) empty.style.display = "block";
    if (diagram) diagram.style.display = "none";
    if (titleEl) titleEl.textContent = title;
    if (descEl) descEl.textContent = description;
  };

  const _checkAndShowDiagram = () => {
    const diagram = document.getElementById("ow-diagram");
    const ready = _isConfigReady();

    if (ready) {
      _showDiagramContent();
      if (diagram) diagram.style.display = "block";
    } else {
      _showEmptyState();
    }

    return ready;
  };

  const _showShimmer = () => {
    _showDiagramContent();

    const wrapper = document.querySelector("#ow-well-diagram-panel .well-diagram-wrapper");
    const shimmer = document.getElementById("ow-well-diagram-shimmer");
    const lastUpdateShimmer = document.getElementById(
      "ow-well-diagram-last-update-shimmer",
    );
    const lastUpdate = document.getElementById("ow-last-update-fw");
    const diagram = document.getElementById("ow-diagram");

    if (wrapper) wrapper.classList.add("is-loading");
    if (shimmer) {
      shimmer.classList.remove("is-hidden");
      shimmer.setAttribute("aria-busy", "true");
    }
    if (lastUpdateShimmer) lastUpdateShimmer.style.display = "block";
    if (lastUpdate) lastUpdate.classList.add("is-hidden");
    if (diagram) diagram.style.display = "none";
  };

  const _hideShimmer = () => {
    const wrapper = document.querySelector("#ow-well-diagram-panel .well-diagram-wrapper");
    const shimmer = document.getElementById("ow-well-diagram-shimmer");
    const lastUpdateShimmer = document.getElementById(
      "ow-well-diagram-last-update-shimmer",
    );
    const lastUpdate = document.getElementById("ow-last-update-fw");

    if (wrapper) wrapper.classList.remove("is-loading");
    if (shimmer) {
      shimmer.classList.add("is-hidden");
      shimmer.setAttribute("aria-busy", "false");
    }
    if (lastUpdateShimmer) lastUpdateShimmer.style.display = "none";
    if (lastUpdate) lastUpdate.classList.remove("is-hidden");
  };

  const _setText = (id, text, animate = false) => {
    const sel = d3.select(`#ow-diagram #${id}`);
    if (sel.empty()) return;
    if (animate) {
      sel.transition()
        .duration(DashboardConfig.well.transitionDuration)
        .text(text);
    } else {
      sel.text(text);
    }
  };

  const _setTransform = (id, dy, animate = false) => {
    const sel = d3.select(`#ow-diagram #${id}`);
    if (sel.empty()) return;
    const value = `translate(0, ${dy})`;
    if (animate) {
      sel.transition()
        .duration(DashboardConfig.well.transitionDuration)
        .attr("transform", value);
    } else {
      sel.attr("transform", value);
    }
  };

  const _connectorPath = (anchorX, anchorY, cardEdgeX, cardY, side) => {
    if (Math.abs(anchorY - cardY) < 10) {
      return `M ${anchorX} ${anchorY} L ${cardEdgeX} ${cardY}`;
    }

    const kneeX =
      side === "left" ? anchorX - KNEE_INSET : anchorX + KNEE_INSET;

    return `M ${anchorX} ${anchorY} L ${kneeX} ${anchorY} L ${kneeX} ${cardY} L ${cardEdgeX} ${cardY}`;
  };

  const _updateCallout = (key, anchorY, cardCenterY, animate) => {
    const cfg = CALLOUTS[key];
    const path = _connectorPath(
      cfg.anchorX,
      anchorY,
      cfg.cardEdgeX,
      cardCenterY,
      cfg.side,
    );
    const cardDy = cardCenterY - cfg.defaultCenterY;

    _setTransform(cfg.groupId, cardDy, animate);

    const connector = d3.select(`#ow-diagram #${cfg.connectorId}`);
    const anchor = d3.select(`#ow-diagram #${cfg.anchorId}`);

    if (!connector.empty()) {
      if (animate) {
        connector
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("d", path);
      } else {
        connector.attr("d", path);
      }
    }

    if (!anchor.empty()) {
      if (animate) {
        anchor
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("cx", cfg.anchorX)
          .attr("cy", anchorY);
      } else {
        anchor.attr("cx", cfg.anchorX).attr("cy", anchorY);
      }
    }
  };

  const _updateLastReading = (timestamp) => {
    const el = document.getElementById("ow-last-update-fw");
    if (!el || !timestamp) return;

    const dateStr = timestamp.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const timeStr = timestamp
      .toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
      .replace(".", ":");

    el.textContent = `Last Update: ${dateStr}  ${timeStr}`;
  };

  const _applyStaticLabels = (animate = false) => {
    if (_wellConfig.boreholeDepth) {
      _setText(
        "ow-depth-value",
        `${_fmt(_wellConfig.boreholeDepth)} m`,
        animate,
      );
    }
    _setText(
      "ow-top-pipe-elev",
      _wellConfig.topPipeElevation != null
        ? `${_fmt(_wellConfig.topPipeElevation)} mdpl`
        : "- mdpl",
      animate,
    );
  };

  const _applyReading = (animate = false) => {
    if (!_wellConfig.boreholeDepth) return;

    _applyStaticLabels(animate);
    if (!_reading) return;

    const wlM = Math.max(0, Math.min(_reading.waterLevel, _wellConfig.boreholeDepth));
    const surfaceY = _depthToY(wlM);
    const waterHeight = LAYOUT.wellBottom - surfaceY;
    const unit = _wellConfig.unitDisplay || "m";

    let elevM = _reading.waterLevelElevation;
    if (elevM == null && _wellConfig.topPipeElevation != null) {
      elevM = _wellConfig.topPipeElevation - wlM;
    }

    const centers = _layoutPair(
      surfaceY,
      surfaceY,
      CALLOUTS.elev.cardHeight,
      CALLOUTS.wl.cardHeight,
    );

    const waterSel = d3.select("#ow-diagram #ow-water-fill");
    if (!waterSel.empty()) {
      if (animate) {
        waterSel
          .transition()
          .duration(DashboardConfig.well.transitionDuration)
          .attr("y", surfaceY)
          .attr("height", Math.max(0, waterHeight));
      } else {
        waterSel.attr("y", surfaceY).attr("height", Math.max(0, waterHeight));
      }
    }

    const surfaceDy = surfaceY - LAYOUT.waterDefaultY;
    _setTransform("ow-water-surface-group", surfaceDy, animate);

    _updateCallout("elev", surfaceY, centers.a, animate);
    _updateCallout("wl", surfaceY, centers.b, animate);

    _setText(
      "ow-label-elev-value",
      elevM != null ? `${_fmt(elevM)} mdpl` : "- mdpl",
      animate,
    );
    _setText("ow-label-wl-value", `${_fmt(wlM)} ${unit}`, animate);
    _updateLastReading(_reading.timestamp);
  };

  const _loadTemplate = async () => {
    const container = document.getElementById("ow-diagram");
    if (!container) return false;

    if (_templateLoaded && container.querySelector("svg")) {
      return true;
    }

    container.innerHTML = "";
    const doc = await d3.xml(SVG_URL);
    container.appendChild(document.importNode(doc.documentElement, true));
    _templateLoaded = true;
    return true;
  };

  const _fetchWellConfiguration = async (deviceId) => {
    try {
      const config = await DashboardAPI.getOwWellConfig(deviceId);
      if (!config) {
        _wellConfig = {
          boreholeDepth: null,
          topPipeElevation: null,
          unitDisplay: "m",
        };
        return false;
      }

      const unit = config.unitDisplay || "m";
      const boreholeM = Number(config.boreholeDepth);
      _wellConfig = {
        // Borehole depth is stored in meters (same as station info form).
        boreholeDepth: Number.isFinite(boreholeM) && boreholeM > 0 ? boreholeM : null,
        topPipeElevation:
          config.topPipeElevation != null
            ? Number(config.topPipeElevation)
            : null,
        unitDisplay: unit,
      };

      return _isConfigReady();
    } catch (error) {
      _wellConfig = {
        boreholeDepth: null,
        topPipeElevation: null,
        unitDisplay: "m",
      };
      return false;
    }
  };

  const _fetchWaterLevelData = async (deviceId) => {
    try {
      const data = await DashboardAPI.getOwWellData(deviceId);
      if (!Array.isArray(data) || data.length === 0) return;

      const item = data[0];
      const waterLevelInMeters = _toMeters(
        item.waterLevel,
        _wellConfig.unitDisplay,
      );
      if (waterLevelInMeters == null) return;

      const elev =
        item.waterLevelElevation != null
          ? Number(item.waterLevelElevation)
          : null;

      _reading = {
        timestamp: new Date(moment.parseZone(item.readingAtUtc)),
        waterLevel: waterLevelInMeters,
        waterLevelElevation: Number.isFinite(elev) ? elev : null,
      };
    } catch (error) {
      // keep previous reading if refresh fails
    }
  };

  const _stopPolling = () => {
    if (_pollInterval) {
      clearInterval(_pollInterval);
      _pollInterval = null;
    }
  };

  const _startPolling = () => {
    _stopPolling();
    _pollInterval = setInterval(async () => {
      if (!_deviceId) return;
      await _fetchWaterLevelData(_deviceId);
      if (_reading) _applyReading(true);
    }, DashboardConfig.intervals.dataRefresh);
  };

  const _render = async (animate = false) => {
    const loaded = await _loadTemplate();
    if (!loaded || !_checkAndShowDiagram()) return;
    _applyReading(animate);
  };

  return {
    init() {},

    async loadWellData(deviceId) {
      _deviceId = deviceId;
      _reading = null;
      _templateLoaded = false;
      _stopPolling();
      _showShimmer();

      try {
        const configOk = await _fetchWellConfiguration(deviceId);

        if (!configOk) {
          _showEmptyState();
          return;
        }

        await _fetchWaterLevelData(deviceId);
        await _render(false);
        _hideShimmer();
        _checkAndShowDiagram();
        _startPolling();
      } catch (error) {
        _showEmptyState(
          "Gagal Memuat Ilustrasi",
          "Terjadi kesalahan saat memuat data ilustrasi observation well. Silakan coba lagi.",
        );
      }
    },

    reset() {
      _deviceId = null;
      _reading = null;
      _templateLoaded = false;
      _wellConfig = {
        boreholeDepth: null,
        topPipeElevation: null,
        unitDisplay: "m",
      };
      _stopPolling();
      _hidePanel();
    },

    handleResize() {
      if (_wellConfig.boreholeDepth && _reading) {
        _applyReading(false);
      }
    },

    stop() {
      _stopPolling();
    },

    hide() {
      this.reset();
    },

    start() {
      _startPolling();
    },
  };
})();

window.OwAnimationModule = OwAnimationModule;
